import React, { useState } from 'react';
import { 
  FaShieldAlt, 
  FaLock, 
  FaUserSecret, 
  FaCookie, 
  FaEnvelope, 
  FaMobileAlt,
  FaDatabase,
  FaGlobe,
  FaCheckCircle,
  FaArrowLeft,
  FaHome,
  FaPrint,
  FaDownload
} from 'react-icons/fa';

const PrivacyPage = () => {
  const [lastUpdated] = useState('April 15, 2026');

  const sections = [
    {
      id: 'information',
      title: 'Information We Collect',
      icon: <FaDatabase className="w-5 h-5" />,
      content: [
        {
          subtitle: 'Personal Information',
          text: 'When you create an account, place an order, or contact us, we may collect:',
          points: [
            'Name, email address, phone number, and shipping/billing address',
            'Payment information (processed securely through our payment partners)',
            'Account credentials (username and encrypted password)',
            'Order history and preferences'
          ]
        },
        {
          subtitle: 'Automatically Collected Information',
          text: 'When you visit our website, we automatically collect:',
          points: [
            'IP address and device information',
            'Browser type and version',
            'Pages you visit and time spent on our site',
            'Referring website addresses'
          ]
        }
      ]
    },
    {
      id: 'usage',
      title: 'How We Use Your Information',
      icon: <FaGlobe className="w-5 h-5" />,
      content: [
        {
          text: 'We use the information we collect to:',
          points: [
            'Process and fulfill your orders',
            'Communicate with you about your orders and account',
            'Send you promotional offers and updates (with your consent)',
            'Improve our website and customer experience',
            'Prevent fraud and enhance security',
            'Comply with legal obligations'
          ]
        }
      ]
    },
    {
      id: 'sharing',
      title: 'Information Sharing',
      icon: <FaUserSecret className="w-5 h-5" />,
      content: [
        {
          text: 'We do not sell your personal information. We may share your information with:',
          points: [
            'Shipping partners to deliver your orders',
            'Payment processors to handle transactions',
            'Service providers who assist our business operations',
            'Law enforcement when required by law'
          ]
        }
      ]
    },
    {
      id: 'cookies',
      title: 'Cookies & Tracking',
      icon: <FaCookie className="w-5 h-5" />,
      content: [
        {
          text: 'We use cookies and similar technologies to:',
          points: [
            'Remember your preferences and shopping cart',
            'Analyze website traffic and usage patterns',
            'Personalize your shopping experience',
            'Show relevant product recommendations'
          ]
        }
      ]
    },
    {
      id: 'security',
      title: 'Data Security',
      icon: <FaLock className="w-5 h-5" />,
      content: [
        {
          text: 'We implement robust security measures to protect your information:',
          points: [
            'SSL encryption for all data transmissions',
            'Regular security audits and updates',
            'Restricted access to personal information',
            'Secure payment gateway integration'
          ]
        }
      ]
    },
    {
      id: 'rights',
      title: 'Your Rights',
      icon: <FaCheckCircle className="w-5 h-5" />,
      content: [
        {
          text: 'You have the right to:',
          points: [
            'Access your personal information',
            'Correct inaccurate information',
            'Request deletion of your data',
            'Opt-out of marketing communications',
            'Export your data in a portable format'
          ]
        }
      ]
    }
  ];

  const contactInfo = {
    email: 'info@skkjewellers.com',
    phone: '+91 92856 68748',
    address: 'SKK Jewelry, 123 Fashion Street, Mumbai - 400001, India'
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-amber-50">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-purple-700 via-pink-700 to-amber-700 text-white">
        <div className="container mx-auto px-4 py-8 md:py-12">
          {/* Navigation Links */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-4">
              <a href="/" className="flex items-center gap-2 text-white/80 hover:text-white transition-colors">
                <FaHome className="w-4 h-4" />
                <span className="text-sm">Home</span>
              </a>
              <span className="text-white/40">/</span>
              <span className="text-sm font-medium">Privacy Policy</span>
            </div>
            <div className="flex gap-3">
              <button 
                onClick={() => window.print()}
                className="flex items-center gap-2 px-3 py-1.5 bg-white/20 rounded-lg hover:bg-white/30 transition-all text-sm"
              >
                <FaPrint className="w-3.5 h-3.5" />
                Print
              </button>
              <button className="flex items-center gap-2 px-3 py-1.5 bg-white/20 rounded-lg hover:bg-white/30 transition-all text-sm">
                <FaDownload className="w-3.5 h-3.5" />
                Download PDF
              </button>
            </div>
          </div>

          {/* Main Header */}
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-4">
              <FaShieldAlt className="w-4 h-4" />
              <span className="text-sm font-semibold">Privacy Policy</span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-3">
              Your Privacy Matters
            </h1>
            <p className="text-purple-100 max-w-2xl mx-auto text-sm md:text-base">
              We are committed to protecting your personal information and being transparent about how we use it.
            </p>
            <div className="mt-4 text-sm text-purple-200">
              Last Updated: {lastUpdated}
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 md:py-12 max-w-6xl">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <div className="sticky top-8 bg-white rounded-xl shadow-md p-4 border border-purple-100">
              <h3 className="font-bold text-gray-800 mb-3 pb-2 border-b border-gray-200">
                On This Page
              </h3>
              <nav className="space-y-1">
                {sections.map((section) => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-all duration-200"
                  >
                    <span className="text-purple-500">{section.icon}</span>
                    <span>{section.title}</span>
                  </a>
                ))}
                <a
                  href="#contact"
                  className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-all duration-200"
                >
                  <FaEnvelope className="w-4 h-4 text-purple-500" />
                  <span>Contact Us</span>
                </a>
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-xl shadow-md overflow-hidden border border-purple-100">
              {/* Introduction */}
              <div className="p-6 md:p-8 border-b border-gray-200 bg-gradient-to-r from-purple-50/30 to-pink-50/30">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center">
                    <FaShieldAlt className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                    Privacy Policy
                  </h2>
                </div>
                <p className="text-gray-600 leading-relaxed">
                  At SKK Jewelry, we value your trust and are committed to protecting your privacy. 
                  This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
                  when you visit our website or make a purchase from our store.
                </p>
                <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-amber-800 text-sm">
                    <strong>✨ Quick Note:</strong> We never sell your personal information to third parties. 
                    Your data is used solely to provide you with the best shopping experience possible.
                  </p>
                </div>
              </div>

              {/* Sections */}
              {sections.map((section, index) => (
                <div
                  key={section.id}
                  id={section.id}
                  className="p-6 md:p-8 border-b border-gray-200 scroll-mt-20"
                >
                  <div className="flex items-center gap-3 mb-5">
                    <div className={`w-8 h-8 rounded-full bg-gradient-to-r from-purple-100 to-pink-100 flex items-center justify-center text-purple-600`}>
                      {section.icon}
                    </div>
                    <h3 className="text-lg md:text-xl font-bold text-gray-800">
                      {section.title}
                    </h3>
                  </div>
                  
                  {section.content.map((item, idx) => (
                    <div key={idx} className="space-y-3">
                      {item.subtitle && (
                        <h4 className="font-semibold text-purple-700 text-sm uppercase tracking-wide">
                          {item.subtitle}
                        </h4>
                      )}
                      <p className="text-gray-600 text-sm leading-relaxed">
                        {item.text}
                      </p>
                      <ul className="space-y-2 mt-3">
                        {item.points.map((point, pointIdx) => (
                          <li key={pointIdx} className="flex items-start gap-2 text-sm text-gray-600">
                            <FaCheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                            <span>{point}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              ))}

              {/* Children's Privacy */}
              <div className="p-6 md:p-8 border-b border-gray-200">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-100 to-pink-100 flex items-center justify-center text-purple-600">
                    <FaUserSecret className="w-4 h-4" />
                  </div>
                  <h3 className="text-lg md:text-xl font-bold text-gray-800">
                    Children's Privacy
                  </h3>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Our services are not directed to individuals under the age of 18. We do not knowingly 
                  collect personal information from children. If you become aware that a child has provided 
                  us with personal information, please contact us immediately.
                </p>
              </div>

              {/* Changes to Policy */}
              <div className="p-6 md:p-8 border-b border-gray-200">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-100 to-pink-100 flex items-center justify-center text-purple-600">
                    <FaGlobe className="w-4 h-4" />
                  </div>
                  <h3 className="text-lg md:text-xl font-bold text-gray-800">
                    Changes to This Policy
                  </h3>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">
                  We may update this Privacy Policy from time to time. We will notify you of any changes 
                  by posting the new Privacy Policy on this page and updating the "Last Updated" date. 
                  You are advised to review this Privacy Policy periodically for any changes.
                </p>
              </div>

              {/* Contact Section */}
              <div id="contact" className="p-6 md:p-8 bg-gradient-to-r from-purple-50 to-pink-50 scroll-mt-20">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center">
                    <FaEnvelope className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-lg md:text-xl font-bold text-gray-800">
                    Contact Us
                  </h3>
                </div>
                <p className="text-gray-600 text-sm mb-4">
                  If you have any questions about this Privacy Policy or our data practices, please contact us:
                </p>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-white rounded-lg p-4 text-center shadow-sm border border-purple-100">
                    <FaEnvelope className="w-5 h-5 text-purple-600 mx-auto mb-2" />
                    <p className="text-xs text-gray-500 mb-1">Email Us</p>
                    <a href={`mailto:${contactInfo.email}`} className="text-sm text-purple-600 font-medium hover:underline">
                      {contactInfo.email}
                    </a>
                  </div>
                  <div className="bg-white rounded-lg p-4 text-center shadow-sm border border-purple-100">
                    <FaMobileAlt className="w-5 h-5 text-purple-600 mx-auto mb-2" />
                    <p className="text-xs text-gray-500 mb-1">Call Us</p>
                    <a href={`tel:${contactInfo.phone}`} className="text-sm text-purple-600 font-medium hover:underline">
                      {contactInfo.phone}
                    </a>
                  </div>
                  <div className="bg-white rounded-lg p-4 text-center shadow-sm border border-purple-100">
                    <FaGlobe className="w-5 h-5 text-purple-600 mx-auto mb-2" />
                    <p className="text-xs text-gray-500 mb-1">Visit Us</p>
                    <p className="text-xs text-gray-600">{contactInfo.address}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer Note */}
            <div className="mt-6 text-center text-xs text-gray-500">
              <p>By using our website, you consent to our Privacy Policy and agree to its terms.</p>
              <p className="mt-1">© {new Date().getFullYear()} SKK Jewelry. All rights reserved.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;